import Link from "next/link";
import React, { createElement, FC, useEffect } from "react";
import Prism from "prismjs";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import ReactMarkdown from "react-markdown";

import { linkShortnames, sanitizeUrl } from "src/lib/functions";

const flatten = (text: string, child: any): string =>
  typeof child === "string"
    ? text + child
    : React.Children.toArray(child.props.children).reduce(flatten, text);

// Renderers are merged with the defaults
// https://github.com/remarkjs/react-markdown/blob/main/src/renderers.js
const renderers = {
  link: (props: {
    href?: string;
    children: React.ReactNode & React.ReactNode[];
  }) => {
    const href = sanitizeUrl(props.href ?? "");
    const children = React.Children.toArray(props.children);
    const text = children.reduce(flatten, "");
    if (
      href.startsWith("/") ||
      href.startsWith("https://hash.ai/") ||
      href.startsWith("https://dev.hash.ai/")
    ) {
      return (
        <Link href={href}>
          <a>{text}</a>
        </Link>
      );
    } else if (href.startsWith("#")) {
      return <a href={href}>{children}</a>;
    }

    return (
      <a target="_blank noopener noreferer" href={href}>
        {children}
      </a>
    );
  },
  code: (
    props: React.DetailedHTMLProps<
      React.HTMLAttributes<HTMLElement>,
      HTMLElement
    >
  ) => {
    const { className, children } = props;
    if (className?.includes("video")) {
      return (
        <video autoPlay playsInline muted loop>
          <source src={sanitizeUrl(children?.toString() ?? "")} />
        </video>
      );
    }
    const code = createElement(
      "code",
      className ? { className } : null,
      children
    );
    return code;
  },
  heading: (props: { level: number; children: React.ReactChild[] }) => {
    const children = React.Children.toArray(props.children);
    const text = children.reduce(flatten, "");
    const slug = text.toLowerCase().replace(/\W/g, "-");
    return React.createElement("h" + props.level, { id: slug }, props.children);
  },
};

type MarkdownDisplayProps = {
  text: string;
  escapeHtml?: boolean;
};

const MarkdownDisplay: FC<MarkdownDisplayProps> = ({
  text,
  escapeHtml = true,
}) => {
  useEffect(() => Prism.highlightAll(), [text]);

  const rehypePlugins = escapeHtml ? [] : [rehypeRaw];

  return (
    <ReactMarkdown
      className="markdown-display"
      skipHtml={escapeHtml}
      linkTarget={"_blank noreferrer noopener"}
      components={renderers}
      children={linkShortnames("markdown", text)}
      remarkPlugins={[remarkGfm]}
      rehypePlugins={rehypePlugins}
    />
  );
};

export default MarkdownDisplay;
